import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import logoIcon from '../../../assets/icons/header/oneInformBar/logo.svg';
import cartIcon from '../../../assets/icons/miniBasket/MiniBasketIcon.svg';
import { appConfig } from '../../../config/app-config';
import { useAuth, User, Logout, LogoutPopUp } from '../../../features/auth';
import MiniBasketRaw from '../../../features/cart/components/miniBasket/miniBasket';
import { getCart, subscribeToCartUpdates } from '../../../features/cart/utils/cartUtil';


// MiniBasket is a JS component; items and isOpen are optional (the component uses hook-based fallbacks).
const MiniBasket = MiniBasketRaw as React.ComponentType<{
    children: React.ReactNode;
    eventKey: string;
    items?: unknown[];
    isOpen?: boolean;
}>;

// Minimal shape for a cart line item used only in this component for cart count calculation.
interface CartLineItem {
    id: string;
    quantity: number;
    custom?: { fields?: Record<string, unknown> };
    variant?: { attributes?: Array<{ name: string }> };
}

interface Cart {
    lineItems?: CartLineItem[];
}

const OneInformaBar = () => {
    const {
        loggedIn,
        userInfo,
        isLoading,
        showLogoutPopup,
        handleLogin,
        handleLogout,
        performLogoutConfirm,
        cancelLogout
    } = useAuth();

    const [cartCount, setCartCount] = useState<number>(0);

    useEffect(() => {
        const updateCount = (cart: Cart) => {
            let count = 0;
            if (cart && cart.lineItems && cart.lineItems.length > 0) {
                // Group items by associationKey AND delegateRate
                const groups: Record<string, CartLineItem[]> = {};
                const looseItems: CartLineItem[] = [];

                cart.lineItems.forEach((item: CartLineItem) => {
                    const assocKey = item.custom?.fields?.['associationKey'] as string | undefined;
                    const delegateRate = item.custom?.fields?.['delegateRate'] as string | undefined;
                    if (assocKey && delegateRate) {
                        const groupKey = assocKey + '||' + delegateRate;
                        if (!groups[groupKey]) groups[groupKey] = [];
                        groups[groupKey].push(item);
                    } else if (assocKey) {
                        // fallback: group by assocKey only if no delegateRate
                        if (!groups[assocKey]) groups[assocKey] = [];
                        groups[assocKey].push(item);
                    } else {
                        looseItems.push(item);
                    }
                });

                // Helper to identify if item is a pass (main item)
                const isPassProduct = (item: CartLineItem): boolean => {
                    const hasPassOptions = item.variant?.attributes?.some((attr) => attr.name === 'options');
                    const hasPassBenefits = item.variant?.attributes?.some((attr) => attr.name === 'benefits');
                    const selectedList = item.custom?.fields?.['selectedOptionsList'];
                    const hasSelectedOptionsList = Array.isArray(selectedList) && selectedList.length > 0;
                    return !!(hasPassOptions || hasPassBenefits || hasSelectedOptionsList);
                };

                // Count: Sum only main items' quantities (exclude add-ons)
                Object.values(groups).forEach((group: CartLineItem[]) => {
                    // Find main items in the group (pass products)
                    const mainItems = group.filter((item) => isPassProduct(item));

                    // If no main items found (shouldn't happen, but fallback to first item)
                    const itemsToCount = mainItems.length > 0 ? mainItems : [group[0]];

                    // Sum quantities of main items only
                    itemsToCount.forEach((item) => {
                        count += item.quantity || 0;
                    });
                });

                looseItems.forEach((item) => {
                    count += item.quantity;
                });
            }
            setCartCount(count);
        };

        const fetchMetadata = async () => {
            // Initial Fetch
            const cart = await getCart();
            updateCount(cart as Cart);
        };

        fetchMetadata();

        // Subscribe to updates (including clear events)
        const unsubscribe = subscribeToCartUpdates((cart: unknown) => {
            updateCount(cart as Cart);
        });

        return () => unsubscribe();
    }, []);

    // Reset cart count on logout
    useEffect(() => {
        if (!loggedIn) {
            setCartCount(0);
        }
    }, [loggedIn]);

    const userName: string = userInfo?.given_name || 'User';

    const location = useLocation();
    const pathParts = location.pathname.split('/').filter(Boolean);
    // Extract: /:storeKey/:eventKey/...
    // Default to constants if parts are missing or invalid
    const storeKey = pathParts.length > 0 ? pathParts[0] : appConfig.defaults.storeKey;
    let eventKey = pathParts.length > 1 ? pathParts[1] : appConfig.defaults.eventKey;

    // Safety check: if eventKey is literally "undefined" or "null" (string) or reserved words
    const reservedWords = ['account', 'cart', 'checkout', 'store', 'shared-basket'];
    if (!eventKey || eventKey === 'undefined' || eventKey === 'null' || reservedWords.includes(eventKey)) {
        eventKey = appConfig.defaults.eventKey;
    }

    return (
        <>
            <header id="site-header" className="bg-[#344054] text-white h-[36px] flex px-[20px] py-[8px] min-[576px]:px-[24px] min-[576px]:py-[8px] text-sm font-bold tracking-normal fixed top-0 w-full z-50">
                <div className="w-full max-w-[350px] min-[576px]:max-w-[1240px] mx-auto flex items-center justify-between">
                    {/* Left: Branding */}
                    <div className="flex items-center">
                        <NavLink to={`/${storeKey}/${eventKey}`} end className="no-underline bg-transparent">
                            <img src={logoIcon} alt="Informa" className="w-auto h-4" />
                        </NavLink>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center h-full">


                        {/* Cart */}
                        <div className="flex items-center pr-[10px] md:pr-[17px] border-r border-gray-600 h-[20px] cursor-pointer hover:text-gray-200 relative no-underline text-white">
                            <MiniBasket eventKey={eventKey}>
                                <div className="relative">
                                    {/* Cart icon: 20×20 on md+, smaller on mobile */}
                                    <img src={cartIcon} alt="Cart" className="w-5 h-5 md:w-[20px] md:h-[20px]" />
                                    {cartCount > 0 && (
                                        /* Badge: 10×10 perfect circle, top-right overlay */
                                        <span
                                            className="absolute -top-[3px] -right-[3px] bg-[#DB0000] text-white font-bold rounded-full flex items-center justify-center leading-none"
                                            style={{ width: 10, height: 10, minWidth: 10, fontSize: 8 }}
                                        >
                                            {cartCount}
                                        </span>
                                    )}
                                </div>
                            </MiniBasket>
                        </div>


                        {/* User Action (Login or Welcome Message) */}
                        <User
                            loggedIn={loggedIn}
                            userName={userName}
                            onClick={handleLogin}
                        />

                        {/* Logout Action (Styled like menu item) */}
                        {loggedIn && (
                            <Logout
                                isLoading={isLoading}
                                onClick={handleLogout}
                            />
                        )}

                    </div>
                </div>

                <LogoutPopUp
                    isOpen={showLogoutPopup}
                    onConfirm={performLogoutConfirm}
                    onCancel={cancelLogout}
                />
            </header>
            {/* Spacer to prevent content jump due to fixed header */}
            <div className="h-[36px]"></div>
        </>
    );
};

export default OneInformaBar;
